/**
 * Ask vs pass-along (kanban 196e9d9b). A pass-along hands one piece of an
 * item to the desk that owns it ("the credit hold is accounting's, not
 * mine"): it is sent once, nobody is reminded, the sender is not waiting on
 * it, and the item says plainly whether Merlin's "already known?" check let
 * it through, is still running, or held it.
 *
 * Pure logic (repo convention: logic in .ts, components in .tsx).
 */
import type { DoorAskHandoffRead, DoorDataRequestRead } from '../../services/qcpDataRequests';

type KindFields = Partial<Pick<DoorDataRequestRead, 'kind' | 'handoff' | 'status'>>;

/** A pass-along, whatever its status. Older rows (no kind) are asks. */
export function isPassAlong(r: KindFields): boolean {
  return r.kind === 'pass_along';
}

/** An open ask the sender is waiting on: a pass-along never counts. */
export function isWaitingAsk(r: KindFields): boolean {
  return r.status === 'open' && !isPassAlong(r);
}

/** A pass-along that still shows on the item as handed (sent, or held back
 *  for Merlin's check — a `proposed` pass-along is NOT a draft). */
export function isHandedPassAlong(r: KindFields): boolean {
  return isPassAlong(r) && (r.status === 'open' || r.status === 'proposed');
}

/** A draft Merlin wrote for the sender to send: never a pass-along. */
export function isDraft(r: KindFields): boolean {
  return r.status === 'proposed' && !isPassAlong(r);
}

/** What Merlin's check did with a pass-along, in the sender's words. */
export function handoffWords(h: DoorAskHandoffRead | null | undefined, who: string): string {
  if (!h) return 'delivered';
  const points = (h.known_points ?? []).filter(Boolean);
  const have = points.length > 0 ? ` (they already have: ${points.join('; ')})` : '';
  switch (h.state) {
    case 'pending':
      return `not sent yet · Merlin is checking whether ${who} already has this`;
    case 'held':
      return `held · Merlin judged ${who} already has this${have}`;
    case 'failed':
      return `could not be sent · ${h.detail ?? 'the send failed'}`;
    case 'superseded':
      return 'not sent · the item closed first';
    default:
      if (h.checked && h.verdict === 'already_known') return `delivered · Merlin's check: ${who} already had it${have}`;
      if (h.checked && h.verdict === 'partly_known') return `delivered · Merlin's check: ${who} had part of it`;
      if (h.checked) return `delivered · Merlin's check: new to ${who}`;
      return h.mode === 'shadow' ? 'delivered · Merlin is checking what was new to them' : 'delivered';
  }
}

/** Whether the sender can push a pass-along out past the check. */
export function canSendAnyway(r: KindFields): boolean {
  const state = r.handoff?.state;
  return isPassAlong(r) && (state === 'pending' || state === 'held' || state === 'failed');
}
